import { TMap, TId, EventEmitter, persistentStorage } from '@apia/util';
import React from 'react';

export type TElementSelector = string | (() => HTMLElement);

type TMaximizationComponent = {
  isMaximized: boolean;
  selector?: TElementSelector;
};

type TMaximizationPersist = TMap<boolean>;

const maximizationPersist = {
  getState(id: TId) {
    const current = (persistentStorage.maximizationPersist ??
      {}) as TMaximizationPersist;
    return current[id];
  },
  setState(id: TId, isMaximized: boolean) {
    // if (!conf<boolean>('lists.persistMaximized', true, 'boolean')) return;
    const current = (persistentStorage.maximizationPersist ??
      {}) as TMaximizationPersist;
    current[id] = isMaximized;
    persistentStorage.maximizationPersist = current;
  },
};
class Maximization extends EventEmitter<{
  toggle: { id: TId; isMaximized?: boolean };
}> {
  components: TMap<TMaximizationComponent> = {};

  checkComponentExists(id: TId) {
    return this.components[id] === undefined;
  }

  emit<K extends 'toggle'>(
    eventName: K,
    params: {
      toggle: {
        id: TId;
        isMaximized?: boolean;
      };
    }[K],
  ): void {
    if (eventName === 'toggle') {
      const { id, isMaximized } = params;
      this.checkComponentExists(id);
      if (!this.components[id]) return;

      this.components[id].isMaximized =
        isMaximized ?? !this.components[id].isMaximized;

      maximizationPersist.setState(id, this.components[id].isMaximized);

      this.isMaximized(id);
      super.emit(eventName, {
        id,
        isMaximized: this.components[id].isMaximized,
      });
    }
  }

  isMaximized(id: TId) {
    this.checkComponentExists(id);
    const selector = this.components[id]?.selector;
    if (selector === undefined) return undefined;

    const content =
      typeof selector === 'string'
        ? (document.querySelector(selector) as HTMLElement)
        : selector?.();
    if (content)
      content.classList.toggle('maximized', this.components[id].isMaximized);
    return this.components[id]?.isMaximized;
  }

  registerMaximizableComponent(
    id: TId,
    selector?: TElementSelector,
    // isDefaultMaximized?: boolean,
  ) {
    this.components[id] = {
      selector: selector ?? this.components[id]?.selector,
      isMaximized: false,
      // isDefaultMaximized ??
      // (conf<boolean>('lists.persistMaximized', true, 'boolean')
      //   ? maximizationPersist.getState(id)
      //   : undefined) ??
      // isConfDefaultMaximized,
    };
  }

  toggleIsMaximized(id: TId, isMaximized?: boolean) {
    this.emit('toggle', { id, isMaximized });
  }
}
export const maximizationController = new Maximization();

/**
 *
 * @returns
 *  **isMaximized**: boolean, indica si el listado está maximizado o no.
 *
 *  **toggleIsMaximized**: (isMaximized?: boolean) => void, alterna el estado
 *  entre true y false si isMaximized===undefined, o setea el estado pasado en
 *  isMaximized en caso contrario.
 */
export default function useIsMaximized(id: TId) {
  React.useEffect(() => {
    maximizationController.registerMaximizableComponent(id);
  }, [id]);

  const [isInnerMaximized, innerSetIsMaximized] = React.useState(
    maximizationController.isMaximized(id) ?? false,
  );

  React.useEffect(() => {
    const handleToggle = ({
      id: eventId,
      isMaximized: eventIsMaximized,
    }: {
      id: TId;
      isMaximized?: boolean;
    }) => {
      if (id === eventId)
        innerSetIsMaximized((current) => eventIsMaximized ?? !current);
    };
    const unsuscribe1 = maximizationController.on('toggle', handleToggle);
    innerSetIsMaximized(
      maximizationController.components[id].isMaximized ?? false,
    );

    return () => {
      unsuscribe1();
    };
  }, [id]);

  return {
    isMaximized: isInnerMaximized,
    toggleIsMaximized: React.useCallback(
      (isMaximized?: boolean) => {
        maximizationController.emit('toggle', { id, isMaximized });
      },
      [id],
    ),
  };
}
